/**
 * Integrytas Group - Field Lineage (FLN)
 * Page: FLN.FIELD_SOURCE — the data-provenance registry + worklist.
 *
 * Every field discovered on every screen (auto-registered as pages render) lands
 * here. Best-effort auto-resolution fills source table.column where it can; the
 * rest form a worklist an administrator maps + describes so end-users can see, in
 * Inspect mode, where each field's data comes from (for report/view building).
 *
 * §17-conformant: PersonalizationProvider + toggle, PersonalizedGrid, filter bar
 * with multi-value facets + Clear all, centered Modal edit. Writes route through
 * the Common Routine (§2); translations follow the Translation-Mapping pattern (§8).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useLookupNames } from '../../lib/use_lookup_names';
import { localeName } from '../../i18n/locale_display';
import { apiDb } from '../../lib/api_client';
import { executeCommonRoutine } from '../../lib/common_routine';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';

// Resolution-status code → badge variant; labels come localized from the lookup _tl.
const STATUS_VARIANT: Record<string, StatusVariant> = {
  RESOLVED: 'success',
  NEEDS_DESCRIPTION: 'warning',
  UNRESOLVED: 'danger',
};

const PAGE_CODE = 'FLN.FIELD_SOURCE';

interface FlnRow {
  fln_field_source_id:    number;
  page_code:              string;
  component_key:          string;
  field_key:              string;
  label:                  string | null;
  source_table:           string | null;
  source_column:          string | null;
  data_type:              string | null;
  is_custom:              'Y' | 'N';
  source_kind_code:       string;
  resolution_status_code: string;
  is_manual:              'Y' | 'N';
  tech_hint:              string | null;
}

interface TlRow { locale_code: string; description: string; derivation_notes: string }
type LabelMap = Record<string, string>;

const GRID_COLS: GridColumnDef[] = [
  { key: 'field_key',         header: 'Field',     defaultWidth: 180 },
  { key: 'label',             header: 'Label',     defaultWidth: 160 },
  { key: 'page_code',         header: 'Page',      defaultWidth: 170 },
  { key: 'source',            header: 'Source',    defaultWidth: 200 },
  { key: 'source_kind',       header: 'Kind',      defaultWidth: 130 },
  { key: 'resolution_status', header: 'Status',    defaultWidth: 130 },
];

export function FlnFieldSourcePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <FlnFieldSourceInner session={session} />
    </PersonalizationProvider>
  );
}

function FlnFieldSourceInner({ session }: { session: AuthSession }) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const pageNames = useLookupNames('csm_page_master', 'csm_page_master_tl');
  const [rows, setRows] = useState<FlnRow[]>([]);
  const [kindLabels, setKindLabels] = useState<LabelMap>({});
  const [statusLabels, setStatusLabels] = useState<LabelMap>({});
  const [locales, setLocales] = useState<string[]>(['EN_US']);
  const [filter, setFilter] = useState('');
  const [facets, setFacets] = useState<{ status: string[]; kind: string[]; page: string[] }>({ status: [], kind: [], page: [] });
  const [editing, setEditing] = useState<FlnRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §17.B13 related-children disclosure. Per-field _tl descriptions are loaded in
  // the editor modal, not on this list, so the control renders inert for now.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Resolve a lookup tl map for the active locale (fallback EN_US).
  const buildLabelMap = (data: { code: string; locale_code: string; display_text: string }[]): LabelMap => {
    const m: LabelMap = {};
    for (const r of data) if (r.locale_code === 'EN_US') m[r.code] = r.display_text;
    for (const r of data) if (r.locale_code === activeLocale) m[r.code] = r.display_text;
    return m;
  };

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [fields, kinds, statuses, langs] = await Promise.all([
        apiDb.from('fln_field_source').select('fln_field_source_id, page_code, component_key, field_key, label, source_table, source_column, data_type, is_custom, source_kind_code, resolution_status_code, is_manual, tech_hint').order('page_code').order('field_key'),
        apiDb.from('fln_source_kind_tl').select('code, locale_code, display_text'),
        apiDb.from('fln_resolution_status_tl').select('code, locale_code, display_text'),
        apiDb.from('csm_language_master').select('internal_code').eq('is_active', 'Y').order('internal_code'),
      ]);
      if (fields.error) throw fields.error;
      setRows((fields.data ?? []) as FlnRow[]);
      setKindLabels(buildLabelMap((kinds.data ?? []) as never));
      setStatusLabels(buildLabelMap((statuses.data ?? []) as never));
      const ls = (langs.data ?? []).map((l: { internal_code: string }) => l.internal_code);
      setLocales(ls.length ? ls : ['EN_US']);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const reScan = async () => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await apiDb.rpc('fln_resolve_sources');
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const pageOptions = useMemo(() => {
    const set = Array.from(new Set(rows.map(r => r.page_code))).sort();
    return set.map(p => ({ value: p, label: pageNames[p] ?? p }));
  }, [rows, pageNames]);
  const kindOptions = useMemo(() => Object.keys(kindLabels).map(c => ({ value: c, label: kindLabels[c] })), [kindLabels]);
  const statusOptions = useMemo(() => Object.keys(statusLabels).map(c => ({ value: c, label: statusLabels[c] })), [statusLabels]);

  const anyFilter = filter.trim().length > 0 || facets.status.length + facets.kind.length + facets.page.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ status: [], kind: [], page: [] }); };
  const needsAttention = () => setFacets(f => ({ ...f, status: ['UNRESOLVED', 'NEEDS_DESCRIPTION'] }));

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (facets.status.length === 0 || facets.status.includes(r.resolution_status_code)) &&
      (facets.kind.length === 0   || facets.kind.includes(r.source_kind_code)) &&
      (facets.page.length === 0   || facets.page.includes(r.page_code)) &&
      (!q || r.field_key.toLowerCase().includes(q) || (r.label ?? '').toLowerCase().includes(q) || r.page_code.toLowerCase().includes(q) || (r.source_table ?? '').toLowerCase().includes(q)));
  }, [rows, filter, facets]);

  const attentionCount = rows.filter(r => r.resolution_status_code !== 'RESOLVED').length;

  const statusBadge = (code: string) => (
    <StatusBadge variant={STATUS_VARIANT[code] ?? 'neutral'} label={statusLabels[code] ?? code} />
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('fln.admin.title', 'Field Lineage')}
          description={t('fln.list.description', 'Where every field on every screen gets its data from — the provenance registry and mapping worklist.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}
              </button>
              {canChange && (<button type="button" onClick={() => void reScan()} disabled={busy} className="btn-primary flex items-center gap-1.5 disabled:opacity-60">
                <RefreshCw className="h-3.5 w-3.5" /> {t('fln.admin.rescan', 'Re-scan sources')}
              </button>)}
            </>
          }
        />
        <StatStrip
          stats={[
            { value: rows.length, label: t('fln.stat.total', 'Fields') },
            { value: attentionCount, label: t('fln.stat.attention', 'Needs attention') },
          ]}
        />
        {error && (
          <ErrorBanner
            message={t('fln.admin.error_banner', "Couldn't load the field sources.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('fln.admin.search', 'Search field, label, page or table…')}
                className="h-8 w-64 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-sm focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('fln.admin.facet_status', 'Status')} options={statusOptions} selected={facets.status} onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('fln.admin.facet_kind', 'Kind')} options={kindOptions} selected={facets.kind} onChange={v => setFacets(f => ({ ...f, kind: v }))} />
            <FacetFilter label={t('fln.admin.facet_page', 'Page')} options={pageOptions} selected={facets.page} onChange={v => setFacets(f => ({ ...f, page: v }))} />
            <ClearFiltersButton active={anyFilter} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <button onClick={needsAttention} title={t('fln.admin.worklist_tip', 'Show fields that still need mapping or a description')}
              className="h-8 rounded-csm-control border border-amber-400 bg-amber-50 px-2 text-csm-sm text-amber-800 hover:bg-amber-100">
              {t('fln.admin.worklist', 'Needs attention')}{attentionCount > 0 ? ` (${attentionCount})` : ''}
            </button>
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS} sourceTable="fln_field_source">
          {(visibleCols, widths) => (
            <table className="min-w-full w-max border-collapse text-csm-sm">
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-2xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-3 py-1.5" title={t('fln.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.edit', 'Edit')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.fln_field_source_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.fln_field_source_id)} hasChildren={hasRelated(r.fln_field_source_id)} onToggle={() => detail.toggle(r.fln_field_source_id)} expandLabel={t('fln.related.expand', 'Show related records')} collapseLabel={t('fln.related.collapse', 'Hide related')} tooltip={t('fln.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'field_key'         && <span className="font-mono text-csm-xs">{r.field_key}</span>}
                        {col.key === 'label'             && (r.label ?? '')}
                        {col.key === 'page_code'         && <span title={r.page_code} className="text-csm-2xs text-csm-watermark">{pageNames[r.page_code] ?? r.page_code}</span>}
                        {col.key === 'source'            && (r.source_table && r.source_column
                          ? <span className="font-mono text-csm-xs">{r.source_table}.{r.source_column}</span>
                          : <span className="text-csm-2xs italic text-csm-watermark">{t('fln.admin.unmapped', 'unmapped')}</span>)}
                        {col.key === 'source_kind'       && <span className="text-csm-2xs">{kindLabels[r.source_kind_code] ?? r.source_kind_code}</span>}
                        {col.key === 'resolution_status' && statusBadge(r.resolution_status_code)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(r)} className="inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(r.fln_field_source_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.fln_field_source_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilter ? 'no-match' : 'no-data'} title={t('fln.admin.empty', 'No fields match your filters. Visit screens (as an admin) to discover their fields, then Re-scan.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {editing && (
        <FlnEditModal
          session={session}
          row={editing}
          locales={locales}
          kindLabels={kindLabels}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT MODAL
// =============================================================================

interface EditProps {
  session: AuthSession;
  row: FlnRow;
  locales: string[];
  kindLabels: LabelMap;
  onClose: () => void;
  onSaved: () => void;
}

function FlnEditModal({ session, row, locales, kindLabels, onClose, onSaved }: EditProps) {
  const { t } = useTranslation();
  const pageNames = useLookupNames('csm_page_master', 'csm_page_master_tl');
  const activeLocale = useActiveLocale();
  const localeNameOf = (code: string) => localeName(code, activeLocale);
  const [sourceTable, setSourceTable]   = useState(row.source_table ?? '');
  const [sourceColumn, setSourceColumn] = useState(row.source_column ?? '');
  const [kindCode, setKindCode]         = useState(row.source_kind_code);
  const [tls, setTls] = useState<TlRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb.from('fln_field_source_tl')
        .select('locale_code, description, derivation_notes')
        .eq('fln_field_source_id', row.fln_field_source_id);
      if (cancelled) return;
      const existing = (data ?? []) as TlRow[];
      // Seed a row for every active locale so descriptions are translatable (§8).
      const seeded = locales.map(l => existing.find(e => e.locale_code === l) ?? { locale_code: l, description: '', derivation_notes: '' });
      setTls(seeded.length ? seeded : [{ locale_code: 'EN_US', description: '', derivation_notes: '' }]);
    })();
    return () => { cancelled = true; };
  }, [row.fln_field_source_id, locales]);

  const updateTl = (i: number, patch: Partial<TlRow>) => setTls(prev => prev.map((r, j) => j === i ? { ...r, ...patch } : r));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const tbl = sourceTable.trim() || null;
      const col = sourceColumn.trim() || null;
      const hasDesc = tls.some(r => r.description.trim());
      const status = (tbl && col) ? (hasDesc ? 'RESOLVED' : 'NEEDS_DESCRIPTION') : 'UNRESOLVED';

      // Main row through the Common Routine (§2). is_manual='Y' locks it against re-scan.
      const res = await executeCommonRoutine({
        table_name: 'fln_field_source',
        operation: 'CHANGE',
        key_columns: ['fln_field_source_id'],
        row: {
          fln_field_source_id: row.fln_field_source_id,
          source_table: tbl,
          source_column: col,
          source_kind_code: kindCode,
          resolution_status_code: status,
          is_manual: 'Y',
          modified_by: getForensicActorId(session),
        },
      });
      if (res.result_code !== 'OK') {
        throw new Error(res.errors[0]?.message ?? t('fln.admin.save_failed', 'Could not save the field source.'));
      }

      // Translations: one _tl row per language with any content (§8).
      for (const r of tls) {
        if (!r.locale_code) continue;
        if (!r.description.trim() && !r.derivation_notes.trim()) continue;
        const { error: e } = await apiDb.from('fln_field_source_tl').upsert({
          fln_field_source_id: row.fln_field_source_id,
          locale_code: r.locale_code,
          description: r.description.trim() || null,
          derivation_notes: r.derivation_notes.trim() || null,
          modified_by: getForensicActorId(session),
        }, { onConflict: 'fln_field_source_id,locale_code' });
        if (e) throw e;
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('fln.admin.edit_title', 'Field source — {{field}}', { field: row.field_key })}
      onClose={onClose}
      width="2xl"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
            {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </button>
        </>
      }
    >
      {/* Read-only identity + technical hint to guide the description. */}
      <div className="mb-3 grid grid-cols-2 gap-3 text-csm-sm">
        <Labeled label={t('fln.admin.field', 'Field key')}><span className="font-mono text-csm-xs">{row.field_key}</span></Labeled>
        <Labeled label={t('fln.admin.page', 'Page / component')}><span title={row.page_code} className="text-csm-2xs text-csm-watermark">{pageNames[row.page_code] ?? row.page_code} · {row.component_key || '—'}</span></Labeled>
      </div>
      {row.tech_hint && (
        <div className="mb-3 rounded border border-csm-border bg-csm-surface-alt px-2 py-1.5 text-csm-2xs text-csm-ink-soft">
          <span className="font-semibold uppercase tracking-wide text-csm-watermark">{t('fln.admin.tech_hint', 'Detected')}: </span>{row.tech_hint}
        </div>
      )}

      <div className="mb-3 grid grid-cols-3 gap-3">
        <Labeled label={t('fln.admin.source_table', 'Source table')}>
          <input value={sourceTable} onChange={e => setSourceTable(e.target.value)} placeholder="csm_user_master"
            className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
        </Labeled>
        <Labeled label={t('fln.admin.source_column', 'Source column')}>
          <input value={sourceColumn} onChange={e => setSourceColumn(e.target.value)} placeholder="email"
            className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
        </Labeled>
        <FilteredModalSingleSelect
          label={t('fln.admin.kind', 'Source kind')}
          options={Object.keys(kindLabels).map(c => ({ internal_code: c, display_text: kindLabels[c] }))}
          selectedCode={kindCode || null}
          onChange={(code) => setKindCode(code ?? kindCode)}
          allowClear={false}
        />
      </div>

      {/* Multilingual description + derivation (Translation-Mapping pattern, §8). */}
      <div className="mb-1 text-csm-sm font-medium text-csm-ink-soft">{t('fln.admin.descriptions', 'Plain-English descriptions (per language)')}</div>
      <div className="overflow-hidden rounded border border-csm-border">
        <table className="w-full text-csm-sm">
          <thead className="bg-csm-surface-alt text-csm-2xs uppercase tracking-wide text-csm-ink-soft">
            <tr>
              <th className="px-2 py-1 text-left">{t('fln.admin.language', 'Language')}</th>
              <th className="px-2 py-1 text-left">{t('fln.admin.description', 'What this is (description)')}</th>
              <th className="px-2 py-1 text-left">{t('fln.admin.derivation', 'How it is derived')}</th>
            </tr>
          </thead>
          <tbody>
            {tls.map((r, i) => (
              <tr key={r.locale_code} className="border-t border-csm-border-soft">
                <td className="px-2 py-1 text-csm-xs" title={r.locale_code}>{localeNameOf(r.locale_code)}</td>
                <td className="px-1 py-1"><input value={r.description} onChange={e => updateTl(i, { description: e.target.value })} className="h-7 w-full rounded border border-csm-border px-1 text-csm-sm" /></td>
                <td className="px-1 py-1"><input value={r.derivation_notes} onChange={e => updateTl(i, { derivation_notes: e.target.value })} className="h-7 w-full rounded border border-csm-border px-1 text-csm-sm" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{label}</label>
      {children}
    </div>
  );
}
